/**
 * Ticket constants and configuration
 * Shared between frontend and backend
 */

import { TicketCategory, TicketPriority, TicketStatus } from '../types/ticket.types';

// SLA Configuration by Priority (in minutes)
export const DEFAULT_SLA_CONFIG = {
  [TicketPriority.CRITICAL]: {
    responseTime: 15, // 15 minutes
    resolutionTime: 240, // 4 hours
  },
  [TicketPriority.HIGH]: {
    responseTime: 60, // 1 hour
    resolutionTime: 480, // 8 hours
  },
  [TicketPriority.MEDIUM]: {
    responseTime: 240, // 4 hours
    resolutionTime: 1440, // 24 hours
  },
  [TicketPriority.LOW]: {
    responseTime: 480, // 8 hours
    resolutionTime: 2880, // 48 hours
  },
} as const;

// Status workflow transitions
export const STATUS_TRANSITIONS = {
  [TicketStatus.OPEN]: [TicketStatus.IN_PROGRESS, TicketStatus.RESOLVED, TicketStatus.CLOSED],
  [TicketStatus.IN_PROGRESS]: [TicketStatus.OPEN, TicketStatus.RESOLVED, TicketStatus.CLOSED],
  [TicketStatus.RESOLVED]: [TicketStatus.CLOSED, TicketStatus.REOPENED],
  [TicketStatus.CLOSED]: [TicketStatus.REOPENED],
  [TicketStatus.REOPENED]: [TicketStatus.IN_PROGRESS, TicketStatus.RESOLVED, TicketStatus.CLOSED],
} as const;

// Allowed file types for attachments
export const ALLOWED_FILE_TYPES = {
  IMAGES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  DOCUMENTS: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
  TEXT: ['text/plain', 'text/csv', 'text/markdown'],
  ARCHIVES: ['application/zip', 'application/x-rar-compressed', 'application/x-7z-compressed'],
} as const;

export const ALL_ALLOWED_FILE_TYPES = [
  ...ALLOWED_FILE_TYPES.IMAGES,
  ...ALLOWED_FILE_TYPES.DOCUMENTS,
  ...ALLOWED_FILE_TYPES.TEXT,
  ...ALLOWED_FILE_TYPES.ARCHIVES,
];

// File size limits
export const FILE_SIZE_LIMITS = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_TOTAL_SIZE: 50 * 1024 * 1024, // 50MB per ticket
  MAX_FILES_PER_TICKET: 20,
  MAX_FILES_PER_COMMENT: 5,
} as const;

// Ticket number generation pattern
export const TICKET_NUMBER_PREFIX = 'TKT';
export const TICKET_NUMBER_FORMAT = `${TICKET_NUMBER_PREFIX}-{YYYY}-{####}`;

// Default filters and pagination
export const DEFAULT_FILTERS = {
  status: [],
  priority: [],
  category: [],
  assignedTo: undefined,
  createdBy: undefined,
  search: '',
  startDate: undefined,
  endDate: undefined,
  slaBreached: false,
  myTickets: false,
  watching: false,
  tags: [],
} as const;

export const DEFAULT_PAGINATION = {
  page: 1,
  limit: 25,
  sortBy: 'createdAt',
  sortOrder: 'desc' as 'asc' | 'desc',
} as const;

// Priority labels and colors
export const PRIORITY_CONFIG = {
  [TicketPriority.CRITICAL]: {
    label: 'Critical',
    color: '#f44336',
    bgcolor: '#ffebee',
    weight: 4,
    icon: 'priority_high',
  },
  [TicketPriority.HIGH]: {
    label: 'High',
    color: '#ff9800',
    bgcolor: '#fff3e0',
    weight: 3,
    icon: 'keyboard_arrow_up',
  },
  [TicketPriority.MEDIUM]: {
    label: 'Medium',
    color: '#2196f3',
    bgcolor: '#e3f2fd',
    weight: 2,
    icon: 'remove',
  },
  [TicketPriority.LOW]: {
    label: 'Low',
    color: '#4caf50',
    bgcolor: '#e8f5e8',
    weight: 1,
    icon: 'keyboard_arrow_down',
  },
} as const;

// Status labels and colors
export const STATUS_CONFIG = {
  [TicketStatus.OPEN]: {
    label: 'Open',
    color: '#757575',
    bgcolor: '#f5f5f5',
    icon: 'fiber_new',
  },
  [TicketStatus.IN_PROGRESS]: {
    label: 'In Progress',
    color: '#2196f3',
    bgcolor: '#e3f2fd',
    icon: 'play_circle',
  },
  [TicketStatus.RESOLVED]: {
    label: 'Resolved',
    color: '#4caf50',
    bgcolor: '#e8f5e8',
    icon: 'check_circle',
  },
  [TicketStatus.CLOSED]: {
    label: 'Closed',
    color: '#424242',
    bgcolor: '#f5f5f5',
    icon: 'cancel',
  },
  [TicketStatus.REOPENED]: {
    label: 'Reopened',
    color: '#9c27b0',
    bgcolor: '#f3e5f5',
    icon: 'refresh',
  },
} as const;

// Category labels and colors
export const CATEGORY_CONFIG = {
  [TicketCategory.BUG]: {
    label: 'Bug',
    color: '#f44336',
    bgcolor: '#ffebee',
    icon: 'bug_report',
  },
  [TicketCategory.FEATURE]: {
    label: 'Feature Request',
    color: '#2196f3',
    bgcolor: '#e3f2fd',
    icon: 'new_releases',
  },
  [TicketCategory.SUPPORT]: {
    label: 'Support',
    color: '#ff9800',
    bgcolor: '#fff3e0',
    icon: 'support_agent',
  },
  [TicketCategory.QUESTION]: {
    label: 'Question',
    color: '#9c27b0',
    bgcolor: '#f3e5f5',
    icon: 'help',
  },
  [TicketCategory.OTHER]: {
    label: 'Other',
    color: '#607d8b',
    bgcolor: '#eceff1',
    icon: 'category',
  },
} as const;

// SLA indicator colors and thresholds
export const SLA_CONFIG = {
  WARNING_THRESHOLD: 0.8, // Show warning when 80% of time elapsed
  BREACH_WARNING_TIME: 30, // Send warning 30 minutes before breach
  COLORS: {
    ON_TRACK: '#4caf50',
    WARNING: '#ff9800',
    BREACHED: '#f44336',
  },
} as const;

// Email notification settings
export const EMAIL_TEMPLATES = {
  TICKET_CREATED: 'ticket-created',
  TICKET_ASSIGNED: 'ticket-assigned',
  TICKET_UPDATED: 'ticket-updated',
  TICKET_STATUS_CHANGED: 'ticket-status-changed',
  TICKET_COMMENTED: 'ticket-commented',
  TICKET_RESOLVED: 'ticket-resolved',
  TICKET_CLOSED: 'ticket-closed',
  TICKET_REOPENED: 'ticket-reopened',
  SLA_BREACH_WARNING: 'sla-breach-warning',
  SLA_BREACHED: 'sla-breached',
  DAILY_TICKET_REPORT: 'daily-ticket-report',
} as const;

// Socket.io events
export const SOCKET_EVENTS = {
  TICKET_CREATED: 'ticket:created',
  TICKET_UPDATED: 'ticket:updated',
  TICKET_ASSIGNED: 'ticket:assigned',
  TICKET_STATUS_CHANGED: 'ticket:status_changed',
  TICKET_COMMENTED: 'ticket:commented',
  TICKET_SLA_WARNING: 'ticket:sla_warning',
  TICKET_SLA_BREACHED: 'ticket:sla_breached',
  USER_TYPING: 'ticket:user_typing',
  USER_STOPPED_TYPING: 'ticket:user_stopped_typing',
} as const;

// Validation constants
export const VALIDATION_RULES = {
  TITLE: { MIN: 5, MAX: 200 },
  DESCRIPTION: { MIN: 10, MAX: 5000 },
  COMMENT: { MIN: 1, MAX: 2000 },
  TAGS: { MAX_COUNT: 10, MAX_LENGTH: 50 },
  TICKET_NUMBER_LENGTH: 12,
  MAX_REOPENS: 5,
  MAX_WATCHERS: 50,
} as const;

// Export formats
export const EXPORT_FORMATS = {
  CSV: 'csv',
  EXCEL: 'excel',
  PDF: 'pdf',
} as const;

// Default export fields
export const DEFAULT_EXPORT_FIELDS = [
  'ticketNumber',
  'title',
  'status',
  'priority',
  'category',
  'assignedTo',
  'createdBy',
  'createdAt',
  'updatedAt',
  'sla.breached',
] as const;

// Cron job schedules
export const CRON_SCHEDULES = {
  SLA_CHECK: '*/5 * * * *', // Every 5 minutes
  DAILY_REPORT: '0 9 * * *', // Daily at 9 AM
  CLEANUP_ATTACHMENTS: '0 2 * * 0', // Weekly on Sunday at 2 AM
  ARCHIVE_TICKETS: '0 3 1 * *', // Monthly on 1st at 3 AM
} as const;

// Search configuration
export const SEARCH_CONFIG = {
  MIN_SEARCH_LENGTH: 2,
  MAX_SEARCH_LENGTH: 100,
  DEBOUNCE_DELAY: 300, // milliseconds
  MAX_RESULTS: 100,
} as const;

// Mention pattern for extracting @mentions in comments
export const MENTION_PATTERN = /@\[([^\]]+)\]\(([^)]+)\)/g;

// Time zones
export const DEFAULT_TIMEZONE = 'UTC';

// Cache durations (in seconds)
export const CACHE_DURATIONS = {
  TICKET_STATS: 300, // 5 minutes
  SLA_CONFIG: 3600, // 1 hour
  USER_LIST: 1800, // 30 minutes
} as const;
